/**
 * Rolling Returns Section - Expandable
 * Shows 3-month and 12-month rolling returns chart
 * Helps identify consistency and volatility patterns
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface RollingReturn {
  date: string;
  return3m: number;
  return12m: number;
}

interface RollingReturnsSectionProps {
  data: RollingReturn[];
  defaultExpanded?: boolean;
}

const RollingReturnsSection: React.FC<RollingReturnsSectionProps> = ({
  data,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded border border-gray-200">
          <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(2)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate statistics
  const avg3m = data.reduce((sum, d) => sum + d.return3m, 0) / data.length;
  const avg12m = data.reduce((sum, d) => sum + d.return12m, 0) / data.length;
  const volatility3m = Math.sqrt(
    data.reduce((sum, d) => sum + Math.pow(d.return3m - avg3m, 2), 0) / data.length
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
      >
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          <div className="text-left">
            <h4 className="text-lg font-semibold text-gray-900">Rolling Returns Analysis</h4>
            <p className="text-sm text-gray-600">
              3-month and 12-month rolling return patterns
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="mt-6 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-sm text-gray-600 mb-1">Avg 3-Month</div>
              <div className={`text-2xl font-bold ${avg3m >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {avg3m >= 0 ? '+' : ''}{avg3m.toFixed(2)}%
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <div className="text-sm text-gray-600 mb-1">Avg 12-Month</div>
              <div className={`text-2xl font-bold ${avg12m >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {avg12m >= 0 ? '+' : ''}{avg12m.toFixed(2)}%
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-sm text-gray-600 mb-1">Volatility</div>
              <div className="text-2xl font-bold text-gray-900">
                {volatility3m.toFixed(2)}%
              </div>
            </div>
          </div>

          {/* Chart */}
          <div>
            <h5 className="text-sm font-semibold text-gray-900 mb-3">Rolling Returns Over Time</h5>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="return3m"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  name="3-Month Rolling"
                  dot={false}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="return12m"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  name="12-Month Rolling"
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Insight */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-800">
              <strong>Analysis:</strong> {
                volatility3m < 5
                  ? 'Low volatility indicates stable, consistent returns.'
                  : volatility3m < 10
                  ? 'Moderate volatility suggests normal market fluctuations.'
                  : 'High volatility indicates significant return variations over time.'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RollingReturnsSection;
